import type {
  StudentLibraryAssignmentSortingStrategy,
  StudentLibraryCubit,
} from '@breakoutlearning/firebase-repository/cubits/StudentLibraryCubit'
import {
  ValidLibraryObjectActionState,
  type ValidLibraryObject,
} from '@breakoutlearning/firebase-repository/stores/ValidLibraryObject'
import { LibraryObjectState } from '@breakoutlearning/firebase-repository/types'
import { getRoomStateOutputTotalScore } from '@breakoutlearning/firebase-repository/firestore/RoomStateOutput'
import { toFinalScorePercent } from '@breakoutlearning/firebase-repository/sources-of-truth/percentage'
import { useQuery } from '@tanstack/react-query'
import { AppealOverrideIndicator } from 'components/AppealOverrideIndicator'
import { BreakoutButtonLink } from 'components/design-system/BreakoutButton'
import { BreakoutSearchInput } from 'components/design-system/BreakoutSearchInput'
import {
  BreakoutTableColumnHeader,
  type BreakoutTableColumnHeaderSortableBase,
} from 'components/design-system/BreakoutTableColumnHeader'
import { InternalLink } from 'components/core/InternalLink'
import { CalendarIcon } from 'components/icons/Calendar'
import { ChevronRight } from 'components/icons/ChevronRight'
import { SortAmountDown } from 'components/icons/SortAmountDown'
import { query } from 'config/api'
import { buildUrl } from 'config/routes'
import { useCurrentUser, useRepository } from 'hooks/auth'
import { useGradeExplanationReadiness } from 'hooks/useGradeExplanationReadiness'
import { useRootStore } from 'hooks/rootStore'
import { useQueryParams } from 'hooks/router'
import { useTranslationTyped } from 'i18n/i18n'
import { DateTime } from 'luxon'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useRef, type RefObject } from 'react'
import { EmptyListPlaceholder } from './EmptyListPlaceholder'
import {
  ActionForLibraryObject,
  ChatBubbleButton,
  FilterAssignmentsButton,
} from './components'

type GradeOverrideDisplay = {
  overrideTotalScore: number
  appealOverride: boolean
}

function formatDate(date: Date) {
  const value = DateTime.fromJSDate(date)
  return {
    day: value.toFormat('LLL d, yyyy'),
    time: value.toFormat('h:mm a ZZZZ'),
  }
}

function compareOptionalDates(
  first: Date | undefined,
  second: Date | undefined
) {
  if (!first) return second ? 1 : 0
  if (!second) return -1
  return first.getTime() - second.getTime()
}

function formatSectionDateRange({
  start,
  end,
}: {
  start: DateTime
  end: DateTime
}) {
  const startFormat = start.year === end.year ? 'LLL d' : 'LLL d, yyyy'
  return `${start.toFormat(startFormat)} – ${end.toFormat('LLL d, yyyy')}`
}

const FinalGrade = observer(function FinalGrade({
  libraryObject,
  actionState,
  overrides,
  overridesReady,
  readyGradeExplanations,
}: {
  libraryObject: ValidLibraryObject
  actionState: ValidLibraryObjectActionState
  overrides: Map<string, GradeOverrideDisplay>
  overridesReady: boolean
  readyGradeExplanations: Set<string>
}) {
  const repository = useRepository()
  const rootStore = useRootStore()
  const { tt } = useTranslationTyped()
  const userId = useCurrentUser().uid
  const endOfSessionQuizStatusReady =
    !libraryObject.endOfSessionWindowIsOpen ||
    (libraryObject.quizData !== undefined && libraryObject.slides.length > 0)
  const canShowGrade =
    libraryObject.assignment.data.showFinalGradeToParticipants === true &&
    libraryObject.assignment.data.assignmentGradingScalars !== undefined &&
    libraryObject.libraryObjectState === LibraryObjectState.completed &&
    Boolean(libraryObject.roomState.id) &&
    endOfSessionQuizStatusReady &&
    actionState === ValidLibraryObjectActionState.viewResults

  const override = overrides.get(
    `${libraryObject.assignment.id}/${libraryObject.roomState.id}`
  )
  const overrideTotalScore = override?.overrideTotalScore ?? null
  const useCachedScore =
    canShowGrade && overridesReady && overrideTotalScore === null

  const cachedScore = useQuery({
    queryKey: [
      'room-state-output-total-score',
      libraryObject.roomState.id,
      userId,
    ],
    queryFn: () =>
      getRoomStateOutputTotalScore({
        repository,
        roomStateId: libraryObject.roomState.id,
        userId,
      }),
    enabled: useCachedScore,
  })

  const grade = canShowGrade
    ? toFinalScorePercent(
        overrideTotalScore ?? (useCachedScore ? cachedScore.data : null) ?? null
      )
    : null

  if (
    canShowGrade &&
    (!overridesReady || (useCachedScore && cachedScore.isPending))
  )
    return null

  if (grade === null)
    return (
      <span
        className="text-on-surface-var"
        data-testid="grouped-row-final-grade"
      >
        —
      </span>
    )

  const gradeExplainerRoute = 'gradeExplainer'
  const gradeExplainerParams = {
    sectionId: libraryObject.section.id,
    assignmentId: libraryObject.assignment.id,
    roomStateId: libraryObject.roomState.id,
    userId,
  }
  const gradeExplainerQueryParams = { returnTo: 'assignments' }

  return (
    <div className="flex flex-col items-center gap-1">
      {override?.appealOverride ? (
        <strong
          className="inline-grid grid-cols-[14px_auto_14px] items-center gap-1"
          data-testid="grouped-row-final-grade"
        >
          <span
            className="col-start-2"
            data-testid="grouped-row-final-grade-value"
          >
            {grade}%
          </span>
          <AppealOverrideIndicator className="col-start-3" />
        </strong>
      ) : (
        <strong data-testid="grouped-row-final-grade">{grade}%</strong>
      )}
      {repository.featureFlags.data.gradeExplanationAccessEnabled &&
      readyGradeExplanations.has(
        `${libraryObject.assignment.id}/${libraryObject.roomState.id}`
      ) ? (
        <BreakoutButtonLink
          testId="grouped-row-explain-action"
          kind="tertiary"
          size="small"
          href={buildUrl(
            gradeExplainerRoute,
            gradeExplainerParams,
            gradeExplainerQueryParams
          )}
          onClick={(event) => {
            repository.logEvent('grade_explainer_entry_clicked', {
              source: 'assignment_list',
              assignment_id: libraryObject.assignment.id,
              room_id: libraryObject.roomState.id,
            })
            if (event.metaKey || event.ctrlKey || event.shiftKey) return
            event.preventDefault()
            rootStore.navigateTo(
              gradeExplainerRoute,
              gradeExplainerParams,
              gradeExplainerQueryParams
            )
          }}
        >
          <span className="inline-grid grid-cols-[14px_auto] items-center gap-1">
            <span
              className="col-start-2"
              data-testid="grouped-row-explain-label"
            >
              {tt.student_library.redesign.explain()}
            </span>
          </span>
          <span className="inline-block">
            <ChevronRight size={15} data-testid="grouped-row-explain-chevron" />
          </span>
        </BreakoutButtonLink>
      ) : null}
    </div>
  )
})

const AssignmentRow = observer(function AssignmentRow({
  libraryObject,
  cubit,
  overrides,
  overridesReady,
  readyGradeExplanations,
}: {
  libraryObject: ValidLibraryObject
  cubit: StudentLibraryCubit
  overrides: Map<string, GradeOverrideDisplay>
  overridesReady: boolean
  readyGradeExplanations: Set<string>
}) {
  const { tt } = useTranslationTyped()
  const peerReviews = cubit.roomsWithOutstandingPeerReview

  if (libraryObject.slideDeck.isLoading || peerReviews.isLoading) return null

  const actionState = libraryObject.getActionState({
    hasOutstandingPeerReview: peerReviews.value.includes(
      libraryObject.roomState.id
    ),
  })
  const due = libraryObject.effectiveDueDateForCurrentUser
  const session = libraryObject.scheduledAtDate?.toJSDate()
  const showChatButton =
    cubit.isLoaded &&
    libraryObject.libraryObjectState !== LibraryObjectState.canceled &&
    libraryObject.libraryObjectState !== LibraryObjectState.expired &&
    !libraryObject.requiresPayment &&
    !libraryObject.assignment.isSolo &&
    libraryObject.roomState.isNotEmpty

  return (
    <tr data-testid={`grouped-assignment-${libraryObject.libraryObjectId}`}>
      <td
        className="border-b border-outline-light px-4 py-4 align-middle"
        data-testid="grouped-row-due-date"
      >
        {due && (
          <div className="flex flex-col whitespace-nowrap">
            <strong className="text-label-medium">{formatDate(due).day}</strong>
            <div className="text-body-small text-on-surface-var">
              {formatDate(due).time}
            </div>
          </div>
        )}
      </td>
      <th
        scope="row"
        className="border-b border-outline-light px-4 py-4 text-left font-normal"
      >
        <InternalLink
          to={libraryObject.roomState.id ? 'assignmentWithRoom' : 'assignment'}
          params={{
            assignmentId: libraryObject.assignment.id,
            sectionId: libraryObject.section.id,
            roomStateId: libraryObject.roomState.id,
          }}
          className="focus-ring-floating flex items-center gap-3 rounded-lg"
          data-testid="grouped-row-title-link"
        >
          {libraryObject.slideDeckImageURL && (
            <img
              src={libraryObject.slideDeckImageURL}
              alt=""
              className="h-10 w-10 shrink-0 object-contain"
            />
          )}
          <span className="min-w-0">
            <strong
              className="block text-label-medium text-on-surface"
              data-testid="grouped-row-title"
            >
              {libraryObject.slideDeckName}
            </strong>
            <div className="text-body-small text-on-surface-var">
              {libraryObject.slideDeckTeaser}
            </div>
          </span>
        </InternalLink>
      </th>
      <td className="border-b border-outline-light px-4 py-4 text-body-medium">
        <div
          className="flex items-center gap-2"
          data-testid="grouped-row-group"
        >
          {libraryObject.assignment.isSolo
            ? tt.student_library.redesign.solo()
            : libraryObject.roomState.id
              ? libraryObject.roomState.roomStateName
              : tt.student_library.redesign.not_joined()}
          {showChatButton && <ChatBubbleButton libraryObject={libraryObject} />}
        </div>
      </td>
      <td
        className="border-b border-outline-light px-4 py-4 text-body-medium"
        data-testid="grouped-row-session-date"
      >
        {session ? (
          <div className="flex flex-col whitespace-nowrap">
            <strong>{formatDate(session).day}</strong>
            <div className="text-body-small text-on-surface-var">
              {formatDate(session).time}
            </div>
          </div>
        ) : (
          <span className="text-on-surface-var">
            {tt.student_library.redesign.not_scheduled()}
          </span>
        )}
      </td>
      <td className="border-b border-outline-light px-4 py-4 text-center text-label-medium">
        <FinalGrade
          libraryObject={libraryObject}
          actionState={actionState}
          overrides={overrides}
          overridesReady={overridesReady}
          readyGradeExplanations={readyGradeExplanations}
        />
      </td>
      <td
        className="border-b border-outline-light px-4 py-4 text-center"
        data-testid="grouped-row-action"
      >
        {(actionState !== ValidLibraryObjectActionState.sessionScheduled ||
          !session) && (
          <ActionForLibraryObject
            libraryObject={libraryObject}
            isHeader={false}
            actionState={actionState}
          />
        )}
      </td>
    </tr>
  )
})

export const GroupedAssignmentTable = observer(function GroupedAssignmentTable({
  cubit,
  narrowScrollRef,
}: {
  cubit: StudentLibraryCubit
  narrowScrollRef: RefObject<HTMLDivElement>
}) {
  const { tt } = useTranslationTyped()
  const repository = useRepository()
  const rootStore = useRootStore()
  const params = useQueryParams()
  const tableRef = useRef<HTMLDivElement>(null)
  const visible = cubit.filteredSortedLibraryObjects
  const readyGradeExplanations = useGradeExplanationReadiness(
    repository.featureFlags.data.gradeExplanationAccessEnabled &&
      visible.some(
        (item) =>
          item.assignment.data.showFinalGradeToParticipants === true &&
          item.assignment.data.assignmentGradingScalars !== undefined &&
          item.libraryObjectState === LibraryObjectState.completed
      )
  )
  const overrideResult = useQuery({
    ...query.api.v1.users.me.gradeOverrides.index.queryOptions(),
    enabled: visible.some(
      (item) =>
        item.assignment.data.showFinalGradeToParticipants === true &&
        item.libraryObjectState === LibraryObjectState.completed
    ),
    staleTime: 30_000,
  })
  const overrides = useMemo(() => {
    const scores = new Map<string, GradeOverrideDisplay>()
    if (overrideResult.data && !('error' in overrideResult.data)) {
      for (const override of overrideResult.data) {
        scores.set(`${override.assignmentId}/${override.roomId}`, {
          overrideTotalScore: override.overrideTotalScore,
          appealOverride: override.appealOverride,
        })
      }
    }
    return scores
  }, [overrideResult.data])
  const overridesReady = overrideResult.isSuccess || overrideResult.isError
  const rowsReady =
    !cubit.roomsWithOutstandingPeerReview.isLoading &&
    visible.every((item) => !item.slideDeck.isLoading)

  useEffect(() => {
    if (
      params?.restoreScroll !== 'true' ||
      cubit.libraryObjects.length === 0 ||
      !rowsReady
    )
      return

    const scrollTop = repository.numberStore.get('libraryTableScrollTop')
    const frame = requestAnimationFrame(() => {
      if (scrollTop) {
        const scrollContainer = window.matchMedia('(min-width: 1280px)').matches
          ? tableRef.current
          : narrowScrollRef.current
        scrollContainer?.scrollTo(0, scrollTop)
      }
      rootStore.updateQueryParams({ restoreScroll: undefined })
    })
    return () => cancelAnimationFrame(frame)
  }, [
    cubit.libraryObjects.length,
    params?.restoreScroll,
    repository.numberStore,
    rootStore,
    rowsReady,
    narrowScrollRef,
  ])

  if (cubit.libraryObjects.length === 0) return <EmptyListPlaceholder />

  const hasSearchTerm = cubit.assignmentFilterSearchTerm.length > 0
  const sectionOrder = params?.sectionOrder === 'desc' ? 'desc' : 'asc'
  const sections = cubit.sections.models
    .filter((section) => visible.some((item) => item.section.id === section.id))
    .sort((firstSection, secondSection) => {
      const firstEnd = firstSection.sectionEndDateTime?.toMillis()
      const secondEnd = secondSection.sectionEndDateTime?.toMillis()
      if (firstEnd === undefined) return secondEnd === undefined ? 0 : 1
      if (secondEnd === undefined) return -1
      return (firstEnd - secondEnd) * (sectionOrder === 'asc' ? 1 : -1)
    })
  const sortable: BreakoutTableColumnHeaderSortableBase<StudentLibraryAssignmentSortingStrategy> =
    {
      changeSortingStrategy: (strategy, order) =>
        cubit.changeSortingStrategy(strategy, order),
      currentOrder: cubit.sortingOrder,
      currentSortingStrategy: cubit.sortingStrategy,
    }

  return (
    <section
      className="flex shrink-0 flex-col xl:min-h-0 xl:shrink xl:grow"
      aria-label={tt.library.filtered_assignments()}
      data-testid="student-assignments-table"
    >
      <div className="flex flex-wrap gap-3 px-1 pt-1 pb-5">
        <p className="sr-only">{tt.library.search_filter_description()}</p>
        <BreakoutSearchInput
          name="filter"
          onChange={(event) => cubit.changeSearchTerm(event.target.value)}
          className="min-w-64 grow"
          aria-label={tt.library.search_assignments()}
          placeholder={tt.library.search_assignments()}
          allowClear
          data-testid="student-assignments-search"
        />
        <FilterAssignmentsButton cubit={cubit} />
        <button
          type="button"
          data-testid="section-order-toggle"
          className="focus-ring-floating flex min-h-13 items-center justify-center gap-2 rounded-2xl border border-outline-light px-4 text-label-medium whitespace-nowrap text-on-surface hover:bg-surface-dim"
          onClick={() =>
            rootStore.updateQueryParams(
              { sectionOrder: sectionOrder === 'asc' ? 'desc' : undefined },
              { replaceHistory: true }
            )
          }
        >
          {sectionOrder === 'asc'
            ? tt.student_library.redesign.sections_ending_soonest()
            : tt.student_library.redesign.sections_ending_latest()}
          <SortAmountDown
            size={16}
            className={sectionOrder === 'asc' ? 'rotate-180' : undefined}
          />
        </button>
        <div
          className="sr-only"
          aria-live="polite"
          aria-atomic="true"
          data-testid="student-assignments-search-results"
        >
          {hasSearchTerm &&
            (visible.length > 0
              ? tt.library.search_results_available({ count: visible.length })
              : tt.library.search_no_results_available())}
        </div>
      </div>
      <div
        className="xl:min-h-0 xl:grow xl:overflow-auto"
        data-testid="student-assignments-scroll"
        onScroll={(event) => {
          repository.numberStore.set(
            'libraryTableScrollTop',
            event.currentTarget.scrollTop
          )
        }}
        ref={tableRef}
      >
        {sections.length === 0 && (
          <p
            className="p-8 text-center text-body-large text-on-surface-var"
            data-testid="student-assignments-empty"
          >
            {cubit.assignmentFilterSearchTerm
              ? tt.student_library.no_results_found()
              : tt.student_library.no_current_assignments()}
          </p>
        )}
        {sections.map((section) => {
          const rows = visible.filter((item) => item.section.id === section.id)
          if (cubit.sortingStrategy === 'dueDate') {
            rows.sort(
              (first, second) =>
                compareOptionalDates(
                  first.effectiveDueDateForCurrentUser,
                  second.effectiveDueDateForCurrentUser
                ) * (cubit.sortingOrder === 'asc' ? 1 : -1)
            )
          } else if (cubit.sortingStrategy === 'scheduledAt') {
            rows.sort(
              (first, second) =>
                compareOptionalDates(
                  first.scheduledAtDate?.toJSDate(),
                  second.scheduledAtDate?.toJSDate()
                ) * (cubit.sortingOrder === 'asc' ? 1 : -1)
            )
          }
          const total = new Set(rows.map((item) => item.assignment.id)).size
          const completed = new Set(
            rows
              .filter(
                (item) =>
                  item.libraryObjectState === LibraryObjectState.completed
              )
              .map((item) => item.assignment.id)
          ).size
          const progress = total ? (completed / total) * 100 : 0
          const sectionStart = section.sectionStartDateTime
          const sectionEnd = section.sectionEndDateTime

          return (
            <div
              key={section.id}
              className="mb-9 last:mb-0"
              data-testid={`grouped-section-${section.id}`}
            >
              <div className="flex flex-wrap items-end justify-between gap-3 px-4 pt-5 pb-3">
                <div>
                  <h2
                    id={`assignment-section-${section.id}-title`}
                    className="text-title-large font-bold text-on-surface"
                    data-testid="section-header-class-name"
                  >
                    {section.data.className}
                  </h2>
                  <p
                    id={`assignment-section-${section.id}-details`}
                    className="flex flex-wrap gap-2 text-body-medium text-on-surface-var"
                  >
                    <span data-testid="section-header-instructor">
                      {section.instructor.fullName}
                    </span>
                    <span data-testid="section-header-section-name">
                      {section.data.sectionName}
                    </span>
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-label-medium">
                  {sectionStart && sectionEnd && (
                    <div className="flex items-center gap-2 whitespace-nowrap text-on-surface-var">
                      <CalendarIcon size={15} />
                      <span data-testid="section-header-dates">
                        {formatSectionDateRange({
                          start: sectionStart,
                          end: sectionEnd,
                        })}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <span data-testid="section-header-progress">
                      {tt.student_library.redesign.progress({
                        completed,
                        total,
                      })}
                    </span>
                    <div
                      role="progressbar"
                      aria-valuenow={completed}
                      aria-valuemin={0}
                      aria-valuemax={total}
                      aria-label={section.data.className}
                      className="h-1.5 w-28 overflow-hidden rounded-full bg-surface-dim"
                    >
                      <div
                        className="h-full rounded-full bg-on-surface"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table
                  aria-labelledby={`assignment-section-${section.id}-title assignment-section-${section.id}-details`}
                  className="w-full min-w-[1200px] table-fixed border-separate border-spacing-0 text-left"
                >
                  <colgroup>
                    <col className="w-[13%]" />
                    <col className="w-[29%]" />
                    <col className="w-[15%]" />
                    <col className="w-[15%]" />
                    <col className="w-[12%]" />
                    <col className="w-[16%]" />
                  </colgroup>
                  <thead
                    className="bg-surface-dim text-label-medium text-on-surface-var"
                    data-testid="section-columns"
                  >
                    <tr>
                      <BreakoutTableColumnHeader
                        className="rounded-l-2xl px-4 py-3 [&_button]:bg-transparent"
                        sortable={{ sortingStrategy: 'dueDate', ...sortable }}
                        testId="grouped-column-due-date"
                        label={tt.library.due_date()}
                      />
                      <BreakoutTableColumnHeader
                        className="px-4 py-3 [&_button]:bg-transparent"
                        sortable={{
                          sortingStrategy: 'experienceName',
                          ...sortable,
                        }}
                        testId="grouped-column-assignment"
                        label={tt.library.assignment()}
                      />
                      <th scope="col" className="px-4 py-3">
                        {tt.student_library.redesign.group()}
                      </th>
                      <BreakoutTableColumnHeader
                        className="px-4 py-3 [&_button]:bg-transparent"
                        sortable={{
                          sortingStrategy: 'scheduledAt',
                          ...sortable,
                        }}
                        testId="grouped-column-session-date"
                        label={tt.library.session_date()}
                      />
                      <th scope="col" className="px-4 py-3 text-center">
                        {tt.student_library.redesign.final_grade()}
                      </th>
                      <th
                        scope="col"
                        className="rounded-r-2xl px-4 py-3 text-center"
                      >
                        {tt.library.action()}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((item) => (
                      <AssignmentRow
                        key={item.libraryObjectId}
                        libraryObject={item}
                        cubit={cubit}
                        overrides={overrides}
                        overridesReady={overridesReady}
                        readyGradeExplanations={readyGradeExplanations}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
})
